<script setup lang="ts">
import {computed, onBeforeMount} from 'vue'
import {useStore} from 'vuex'
import {useRoute, useRouter} from 'vue-router'

import MembersList from './MembersList.vue'
import InvitesList from './InvitesList.vue'
import MembersListInviteDialog from './MembersListInviteDialog.vue'
import { can } from '@/composables/useCan'

const store = useStore()
const route = useRoute()
const router = useRouter()

const orgId = route.params.org_id
const org = computed(() => store.getters['org/getCurrent'])
const members = computed(() => store.getters['org/getCurrentMembers'])
const invites = computed(() => store.getters['org/getCurrentInvites'])

const isNewOrg = route.query.new === '1'

const membersLength = computed(() => members.value.length)
const invitesLength = computed(() => invites.value.length)

onBeforeMount(async () => {
  // Fetch org data if not already loaded for this specific org
  if (!org.value || String(org.value.organization_id) !== String(orgId)) {
    await store.dispatch('org/fetchOrg', orgId)
  }

  // If user is not a member of this org yet, redirect to overview to process invitation
  if (org.value && !org.value.is_member) {
    await router.push({
      name: 'AuthOverview',
      query: { redirect: `/organizations/${orgId}/members` }
    })
    return
  }

  await store.dispatch('org/fetchOrgMembers', orgId)
  await store.dispatch('org/fetchOrgInvites', orgId)
})
</script>

<template>
  <div v-if="org">
    <div v-if="isNewOrg" class="mb-4 flex items-start gap-2 rounded-md border border-yellow-300 bg-yellow-50 px-4 py-3 text-sm text-yellow-800">
      <span class="mt-0.5">⚠</span>
      <span>Invite the Primary Contact as an <strong>Admin</strong> here, then set your own permissions or remove entirely.</span>
    </div>

    <div class="mb-4 flex items-center justify-between">
      <h2 class="text-lg font-semibold">Members ({{membersLength}})</h2>
      <MembersListInviteDialog
        v-if="can('org_invite', 'create', { orgId: org.organization_id })"
        :org="org"
        @invited="store.dispatch('org/fetchOrgInvites', orgId)"
      />
    </div>

    <MembersList v-if="membersLength" :members="members" :org="org" :key="members" />

    <div class="mt-6 mb-2 flex items-center">
      <div class="basis-1/2">
        <h3 class="text-base font-bold">Pending invites ({{invitesLength}})</h3>
      </div>
    </div>

    <InvitesList v-if="invitesLength" :invites="invites" :org="org" :key="invites" />
  </div>
</template>
